/**
 * bot-status-lib.ts — Phần THUẦN của "Đang trả lời / Chưa trả lời vì…" một nhân viên AI (GET /ai/bots/:id/status, cột
 * `status` của GET /ai/bots). Dùng chung: thanh trạng thái màn nhân viên AI, chip ở danh sách, danh sách kiểm tra lúc ra mắt.
 */
import type { AiBotStatus, AiMode } from '@/hooks/use-ai'
import { AI_MODE_LABEL } from '@/lib/ai-khung'
import { i18n } from '@/lib/i18n'
import { opensInNewTab } from './open-target-lib'

export type StatusReason = NonNullable<AiBotStatus['reasons']>[number]

/** Mục của màn nhân viên AI mà máy chủ trỏ tới (`fix.section`). */
export type StatusSection = 'van-hanh' | 'kenh' | 'gio-lam' | 'bo-nao' | 'phat-hanh'

/** Mã có nhãn ngắn (chip danh sách); nhãn đọc i18n lúc dùng (botStatus.short.<mã>). */
const SHORT_REASON_CODES = [
  'platform_locked',
  'org_ai_disabled',
  'org_paused',
  'bot_disabled',
  'bot_offline',
  'no_channels',
  'channels_disconnected',
  'mode_manual',
  'no_credit',
  'no_key',
  'key_invalid',
  'model_not_allowed',
  'daily_credit_cap',
  'platform_source_disabled',
  'source_unavailable',
  'turn_quota',
  'external_config_missing',
]

export const shortReason = (r: Pick<StatusReason, 'code' | 'message'>): string =>
  SHORT_REASON_CODES.includes(r.code) ? i18n.t(`aiComp:botStatus.short.${r.code}`) : r.message

/** Lý do chặn đầu tiên (null = đang trả lời / không có lý do chặn). */
export function firstBlocking(status: Pick<AiBotStatus, 'reasons'> | null | undefined): StatusReason | null {
  return status?.reasons?.find((r) => r.blocking !== false) ?? null
}

const asMode = (v: unknown): AiMode | null => (v === 'manual' || v === 'suggest' || v === 'auto' ? v : null)

/** "bản phát hành v3" / "chưa phát hành lần nào". */
export function versionText(v: number | null | undefined): string {
  return v != null ? i18n.t('aiComp:botStatus.version', { v }) : i18n.t('aiComp:botStatus.neverPublished')
}

/**
 * Dòng chính của thanh trạng thái: "Đang trả lời khách · 2 kênh · Gợi ý · bản phát hành v3"
 * hoặc "Chưa trả lời khách: <lý do chặn đầu tiên>".
 */
export function statusHeadline(status: AiBotStatus): string {
  if (!status.answering) {
    const r = firstBlocking(status)
    return i18n.t('aiComp:botStatus.headlineBlocked', { reason: r?.message ?? i18n.t('aiComp:botStatus.unknownReason') })
  }
  const parts = [i18n.t('aiComp:botStatus.headlineAnswering'), i18n.t('aiComp:botStatus.channelCount', { count: status.channels?.length ?? 0 })]
  const m = asMode(status.mode)
  if (m) parts.push(AI_MODE_LABEL[m])
  parts.push(versionText(status.publishedVersion))
  return parts.join(' · ')
}

/** Chip ở danh sách: "Đang trả lời" / "Chưa trả lời: hết credit". Không có status (server cũ) → theo công tắc bật. */
export function statusChip(status: AiBotStatus | null | undefined, enabled: boolean): { tone: 'success' | 'warning' | 'secondary'; text: string; title?: string } {
  if (!status) return enabled ? { tone: 'success', text: i18n.t('aiComp:botStatus.chipOn') } : { tone: 'secondary', text: i18n.t('aiComp:botStatus.chipOff') }
  if (status.answering) return { tone: 'success', text: i18n.t('aiComp:botStatus.chipAnswering') }
  const r = firstBlocking(status)
  return { tone: r?.code === 'bot_disabled' ? 'secondary' : 'warning', text: i18n.t('aiComp:botStatus.chipBlocked', { reason: r ? shortReason(r) : i18n.t('aiComp:botStatus.unknownReason') }), title: r?.message }
}

/** Có cảnh báo không chặn (vd kênh mất kết nối một phần, nháp chưa phát hành). */
export function nonBlockingReasons(status: Pick<AiBotStatus, 'reasons'> | null | undefined): StatusReason[] {
  return (status?.reasons ?? []).filter((r) => r.blocking === false)
}

export type FixAction =
  | { kind: 'section'; section: Exclude<StatusSection, 'phat-hanh'> }
  | { kind: 'publish' }
  /** `newTab` — màn khác (Nguồn AI và lượt AI, Kết nối, nhân viên AI khác…) mở ở tab mới, màn này giữ nguyên. */
  | { kind: 'navigate'; path: string; newTab: boolean }
  | { kind: 'none' }

/**
 * Nút sửa của một lý do: `section` + đường dẫn là chính màn nhân viên AI này (hoặc không có đường dẫn) → mở mục tương ứng
 * (`phat-hanh` = mở hộp Phát hành); đường dẫn khác → mở màn đó (tab mới — open-target-lib); không có gì → không có nút.
 */
export function fixAction(fix: StatusReason['fix'] | null | undefined, botId: string): FixAction {
  if (!fix) return { kind: 'none' }
  const here = !fix.path || fix.path === `/ai/nhan-vien/${botId}`
  const section = fix.section as StatusSection | undefined
  if (section && here) {
    if (section === 'phat-hanh') return { kind: 'publish' }
    if (['van-hanh', 'kenh', 'gio-lam', 'bo-nao'].includes(section)) return { kind: 'section', section: section as Exclude<StatusSection, 'phat-hanh'> }
  }
  if (fix.path && !here) return { kind: 'navigate', path: fix.path, newTab: opensInNewTab(fix.path, { screen: 'staff', botId }) }
  return { kind: 'none' }
}
